import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '../lib/api'
import { useToast } from '../lib/toast'
import { allergenName, asNewAllergens, listNames, unansweredAllergens, type Bootstrap,
  type Child, type DraftAllergen, type EmailLogEntry, type Person, type ProfileAllergen, type ReminderSettings } from '@shared/types'
import { AllergenPicker } from './AllergenPicker'
import { Modal } from './Modal'
import type { Theme, ThemePreference } from '../lib/theme'

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** A handful of US zones plus whatever is already set. Typing one is not a task. */
const ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
]

const hourLabel = (h: number) =>
  h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`

const KIND_LABEL: Record<string, string> = {
  reminder: 'Weekly reminder',
  review_request: 'Review request',
  review_done: 'Review approved',
  test: 'Test',
}

/**
 * The four screens Settings is, in the order a parent needs them.
 *
 * Grouped rather than one section per control: "who gets emailed" and "the
 * children" are both about the family, and splitting them would make seven
 * tabs, several holding one button. Ordered by how often they are opened --
 * children and contacts change, the theme does not.
 */
const SECTIONS = [
  { slug: 'family', label: 'Family', icon: '👪' },
  /*
   * Allergies and restrictions are two tabs, not one.
   *
   * An allergy is a medical fact with a severity that turns the board red; a
   * diet is a choice and a school rule is about a room. No single word covers
   * both without demoting whichever it fits worse -- "Restrictions" makes an
   * anaphylactic peanut allergy sound like a preference, which is the exact
   * conflation the severe/avoid split exists to prevent.
   *
   * They still share one draft and one Save, because the server replaces the
   * whole set in one request.
   */
  { slug: 'allergies', label: 'Allergies', icon: '⚠' },
  { slug: 'restrictions', label: 'Restrictions', icon: '🚫' },
  { slug: 'notifications', label: 'Notifications', icon: '✉' },
  { slug: 'app', label: 'App', icon: '⚙' },
] as const

type SectionSlug = (typeof SECTIONS)[number]['slug']

/** `#allergies` on the URL opens that section; anything else opens the first. */
function sectionFromHash(): SectionSlug {
  try {
    const want = window.location.hash.replace(/^#/, '')
    return SECTIONS.some((s) => s.slug === want) ? (want as SectionSlug) : 'family'
  } catch {
    return 'family'
  }
}

const THEMES: { value: ThemePreference; label: string; hint: string }[] = [
  { value: 'light', label: '☀ Light', hint: 'Always light' },
  { value: 'dark', label: '☾ Dark', hint: 'Always dark' },
  { value: 'auto', label: '◐ Auto', hint: 'Follow the device' },
]

/** The stored rows as the editor wants them: same fields, minus the id. */
const asNew = (rows: ProfileAllergen[]): DraftAllergen[] =>
  rows.map(({ kind, slug, label, severity }) => ({ kind, slug, label, severity }))

interface Props {
  boot: Bootstrap
  theme: Theme
  onPeopleChanged: (people: Person[]) => void
  onReminderChanged: (reminder: ReminderSettings) => void
  onAllergensChanged: (allergens: ProfileAllergen[]) => void
  /** Adding, renaming or archiving a child changes the header's switcher. */
  onChildrenChanged: (children: Child[]) => void
  onFeedback: () => void
  /**
   * Whether there is an unsaved allergen edit. The shell holds the tab, so it
   * is the only place that can stop a half-finished edit being lost to a tap
   * on Plan.
   */
  onUnsavedChange: (dirty: boolean) => void
  onError: (err: unknown, fallback: string) => void
}

/**
 * Who gets emailed, about what, and when.
 *
 * Notifications are opt-in per person rather than one global switch: one of us
 * lives in the app and the other wants to hear only when a week is ready.
 */
export function SettingsPage({
  boot,
  theme,
  onPeopleChanged,
  onReminderChanged,
  onAllergensChanged,
  onChildrenChanged,
  onFeedback,
  onUnsavedChange,
  onError,
}: Props) {
  const toast = useToast()
  const [section, setSection] = useState<SectionSlug>(sectionFromHash)

  /*
   * The section lives in the hash, so the back button steps between them and
   * a link can point at one. replaceState rather than push on the first paint
   * would be wrong here -- this only ever runs from a tap, and a tap is a
   * place to come back to.
   */
  const go = useCallback((slug: SectionSlug) => {
    setSection(slug)
    try {
      window.history.pushState(null, '', `?tab=settings#${slug}`)
    } catch {
      /* A history that refuses still leaves the section switched. */
    }
  }, [])

  useEffect(() => {
    const onPop = () => setSection(sectionFromHash())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const [log, setLog] = useState<EmailLogEntry[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  /* One at a time: a row in edit mode and a row being read look different. */
  const [renaming, setRenaming] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [allergens, setAllergens] = useState<DraftAllergen[]>(() => asNew(boot.allergens))
  const [allergenBusy, setAllergenBusy] = useState(false)
  const [allergenSaved, setAllergenSaved] = useState<string | null>(null)

  const allergensDirty = useMemo(
    () => JSON.stringify(allergens) !== JSON.stringify(asNew(boot.allergens)),
    [allergens, boot.allergens],
  )

  const waiting = unansweredAllergens(allergens)

  /*
   * What the printed card would say, read off the saved rows rather than the
   * draft: the card prints what is stored, so a preview of the draft would be
   * a preview of a card that does not exist yet.
   */
  const cardHasSevere = boot.allergens.some((r) => r.kind === 'child' && r.severity === 'severe')
  const cardLines = useMemo(() => {
    const say = (rows: ProfileAllergen[], how?: (r: ProfileAllergen) => string) =>
      rows.map((r) => (how ? how(r) : allergenName(r))).join(', ')
    const allergies = boot.allergens.filter((r) => r.kind === 'child')
    const diets = boot.allergens.filter((r) => r.kind === 'diet')
    const school = boot.allergens.filter((r) => r.kind === 'school')
    /* Only the ones with a number: the card's whole point is someone to ring. */
    const reachable = boot.people.filter((p) => p.phone.trim() !== '')
    return [
      {
        label: 'Allergies',
        value: allergies.length
          ? say(allergies, (r) => `${allergenName(r)}${r.severity === 'severe' ? ' (severe)' : ''}`)
          : 'None recorded',
        missing: allergies.length === 0,
      },
      {
        label: 'Restrictions',
        value: diets.length ? say(diets) : 'None recorded',
        missing: diets.length === 0,
      },
      {
        label: 'School rules',
        /* "No tree nuts", as the chips say it -- "Tree nuts" alone reads like
           something the child has rather than something the classroom bans. */
        value: school.length
          ? say(school, (r) => `No ${allergenName(r).toLowerCase()}`)
          : 'None recorded',
        missing: school.length === 0,
      },
      {
        label: 'Who to call',
        value: reachable.length
          ? listNames(reachable.map((p) => p.name))
          : 'No phone numbers yet — add one under Family',
        missing: reachable.length === 0,
      },
    ]
  }, [boot.allergens, boot.people])

  /*
   * The draft belongs to a child, and switching child does not unmount this
   * page -- the switcher is in the header, above the tab.
   *
   * Without this the editor kept the previous child's answers under the new
   * child's name: Ada's severe peanut still ticked while the heading read
   * "Bo's lunches", with Save offering to write it onto Bo. That is the one
   * failure this app must not have, and it is why the reset keys on the
   * profile id rather than on `boot.allergens` -- which also changes after a
   * save, and would throw away the answer just given.
   */
  useEffect(() => {
    setAllergens(asNew(boot.allergens))
    setAllergenSaved(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boot.profileId])

  /*
   * Told upwards, because the shell owns the tab and is the only place that
   * can intercept leaving. Cleared on unmount so a guard cannot outlive the
   * screen it was guarding.
   */
  useEffect(() => {
    onUnsavedChange(allergensDirty)
  }, [allergensDirty, onUnsavedChange])
  useEffect(() => () => onUnsavedChange(false), [onUnsavedChange])

  async function saveAllergens() {
    if (allergenBusy || waiting.length > 0) return
    setAllergenBusy(true)
    try {
      const saved = await api.saveAllergens(asNewAllergens(allergens))
      onAllergensChanged(saved)
      setAllergens(asNew(saved))
      setAllergenSaved(
        saved.length === 0
          ? 'Saved. Nothing recorded, so nothing will be flagged.'
          : `Saved. ${saved.length} ${saved.length === 1 ? 'thing' : 'things'} to watch for.`,
      )
      toast.notify('Allergies and restrictions saved.')
    } catch (err) {
      onError(err, 'Could not save those allergens.')
    } finally {
      setAllergenBusy(false)
    }
  }

  const reminder = boot.reminder

  useEffect(() => {
    let cancelled = false
    api
      .notifications()
      .then((r) => {
        if (!cancelled) setLog(r.log)
      })
      .catch(() => {
        // The log is a nicety; the switches above it still work.
        if (!cancelled) setLog([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Whoever is signed in, if they have already been added.
  const me = useMemo(
    () => boot.people.find((p) => p.name.toLowerCase() === boot.session.name.toLowerCase()) ?? null,
    [boot.people, boot.session.name],
  )

  async function run<T>(work: () => Promise<T>, fallback: string): Promise<T | null> {
    if (busy) return null
    setBusy(true)
    try {
      return await work()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : fallback)
      return null
    } finally {
      setBusy(false)
    }
  }

  async function addPerson() {
    const name = newName.trim()
    const email = newEmail.trim()
    if (!name || !email) return
    const people = await run(() => api.addPerson({ name, email }), 'Could not add that person.')
    if (people) {
      onPeopleChanged(people)
      setNewName('')
      setNewEmail('')
      setAdding(false)
      toast.notify(`${name} will be emailed at ${email}.`)
    }
  }

  async function toggle(person: Person, key: 'notifyReminder' | 'notifyReview') {
    const people = await run(
      () => api.updatePerson(person.id, { [key]: !person[key] }),
      'Could not update that.',
    )
    if (people) onPeopleChanged(people)
  }

  /*
   * Saved on blur rather than behind a Save button: it is one field, it is
   * rarely touched, and the rest of this list also takes effect on the tap.
   * Only when it has actually changed, so leaving the field alone is not a
   * write and not a toast.
   */
  async function savePhone(person: Person, phone: string) {
    if (phone.trim() === person.phone) return
    const people = await run(
      () => api.updatePerson(person.id, { phone: phone.trim() }),
      'Could not save that number.',
    )
    if (people) {
      onPeopleChanged(people)
      toast.notify(
        phone.trim()
          ? `${person.name}’s number is on ${boot.profileName}’s allergy card.`
          : `Took ${person.name}’s number off the allergy card.`,
      )
    }
  }

  /*
   * The server already refused a duplicate name and took a rename; only the
   * settings screen never offered one, so a typo at sign-up was permanent.
   */
  async function rename(person: Person, name: string) {
    const next = name.trim()
    if (!next || next === person.name) {
      setRenaming(null)
      return
    }
    const people = await run(
      () => api.updatePerson(person.id, { name: next }),
      'Could not rename them.',
    )
    if (people) {
      onPeopleChanged(people)
      setRenaming(null)
      toast.notify(`${person.name} is now ${next}.`)
    }
  }

  async function remove(person: Person) {
    const people = await run(() => api.deletePerson(person.id), 'Could not remove that person.')
    if (people) {
      onPeopleChanged(people)
      toast.notify(`${person.name} will not be emailed.`)
    }
  }

  /*
   * Always refreshed, whether the send worked or not: a failure is recorded in
   * the log too, and a failure is exactly when someone wants to look at it.
   */
  const refreshLog = () =>
    api
      .notifications()
      .then((r) => setLog(r.log))
      .catch(() => {})

  async function invite(person: Person) {
    const r = await run(() => api.invitePerson(person.id), 'Could not send the invitation.')
    if (r) {
      onPeopleChanged(r.people)
      toast.notify(
        r.resent
          ? `Sent ${person.name} a fresh invitation at ${person.email}.`
          : `Invited ${person.name} at ${person.email}. The link lasts two weeks.`,
      )
    }
  }

  async function sendTest(person: Person) {
    const sent = await run(() => api.sendTestEmail(person.id), 'Could not send that test.')
    if (sent) toast.notify(`Test sent to ${sent.recipient}.`)
    await refreshLog()
  }

  /**
   * Runs the same check the cron runs, right now. Not a bypass: what you want
   * to know is whether it would really have gone out.
   */
  async function checkNow() {
    const outcome = await run(() => api.runReminderNow(), 'Could not run that check.')
    await refreshLog()
    if (!outcome) return
    const why: Record<string, string> = {
      disabled: 'Reminders are off.',
      'not-the-hour': `Nothing to do — it is not ${WEEKDAYS[reminder.weekday]} at ${hourLabel(reminder.hour)} yet.`,
      'already-planned': `Nothing to do — ${outcome.weekStart} is already planned.`,
      'no-recipients': 'Nobody has the weekly reminder switched on.',
    }
    if (outcome.skipped) toast.notify(why[outcome.skipped] ?? 'Nothing to do.')
    else {
      const failed = (outcome.deliveries ?? []).filter((d) => !d.ok)
      if (failed.length) toast.warn(`Tried to remind, but ${failed[0]!.error ?? 'it failed'}.`)
      // Names who, because with several children "a reminder was sent" does
      // not say whether it was about the one you were thinking of.
      else if (outcome.nudgedFor?.length) {
        toast.notify(`Reminder sent about ${listNames(outcome.nudgedFor)} for ${outcome.weekStart}.`)
      } else toast.notify(`Reminder sent for ${outcome.weekStart}.`)
    }
  }

  async function patchReminder(patch: Partial<ReminderSettings>) {
    const next = await run(() => api.setReminder(patch), 'Could not save that.')
    if (next) onReminderChanged(next)
  }

  return (
    <div className="content set-page">
      <header className="set-head">
        <h1>Settings</h1>
        <p className="muted">
          Everything about {boot.profileName}&rsquo;s lunches that is not a lunch.
        </p>
      </header>

      {/*
        One section at a time rather than one long scroll. Seven headings ran
        together because each was a <label>, indistinguishable from the label
        of a field inside it, and a phone had to scroll past all of them to
        reach the last. The section is in the URL, so ?tab=settings#allergies
        is a link worth sending.
      */}
      <nav className="set-nav" role="tablist" aria-label="Settings sections">
        {SECTIONS.map((s) => (
          <button
            key={s.slug}
            role="tab"
            className={`set-tab ${section === s.slug ? 'on' : ''}`}
            aria-selected={section === s.slug}
            onClick={() => go(s.slug)}
          >
            <span aria-hidden="true">{s.icon}</span> {s.label}
          </button>
        ))}
      </nav>

      <div className="set-body">
        {section === 'family' && (
          <section className="set-panel" aria-labelledby="set-family">
            <h2 id="set-family" className="sr-only">Family</h2>
            {/* Named so a test can scope to it: Settings has other Add and Save
                buttons, and an unscoped selector matches all of them. */}
            <div className="field" data-section="children">
              <label>Children</label>
              <ChildList onError={onError} onChanged={onChildrenChanged} />
            </div>

            {/*
              The roster, not the mailing list.
              
              One block used to do four jobs -- who exists, what their address
              is, what they are sent, and their phone number -- under a heading
              that only named the third. Adding a grandparent to the allergy
              card meant finding them under "Who gets emailed" and then hunting
              for the field. The people live here, with the things that are
              true of a person; what each one is sent lives on Notifications,
              with the other sending.
            */}
            <div className="field" data-section="people">
              <label>Parents and carers</label>
              <p className="muted" style={{ fontSize: 12.5, margin: '0 0 8px' }}>
                Everyone who helps with lunches. Their numbers go on{' '}
                {boot.profileName}&rsquo;s{' '}
                <a className="linklike" href="/allergy-card" target="_blank" rel="noopener">
                  card for school
                </a>
                , and what each one is emailed is set under Notifications.
              </p>
              {boot.people.length === 0 ? (
                <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
                  Nobody yet. Add someone and they can be emailed, invited to plan with you, or
                  simply listed as a number to call.
                </p>
              ) : (
                <div className="person-list">
                  {boot.people.map((person) => (
                    <div className="person" key={person.id}>
                      <div className="person-head">
                        {renaming === person.id ? (
                          <RenamePerson
                            person={person}
                            busy={busy}
                            onDone={() => setRenaming(null)}
                            onSave={rename}
                          />
                        ) : (
                          <>
                            <span className="title">
                              {person.name}
                              {me?.id === person.id && <span className="muted"> · you</span>}
                            </span>
                            <div className="spacer" />
                            <button
                              className="btn sm ghost"
                              onClick={() => setRenaming(person.id)}
                              disabled={busy}
                              aria-label={`Rename ${person.name}`}
                            >
                              Rename
                            </button>
                            <button
                              className="btn sm ghost danger"
                              onClick={() => remove(person)}
                              disabled={busy}
                              aria-label={`Remove ${person.name}`}
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                      <div className="person-email">{person.email}</div>
                      {/* The one place a number is asked for. It has no other use:
                          Five Lunches does not text anybody. */}
                      <label className="person-phone">
                        <span className="muted">Phone</span>
                        <input
                          type="tel"
                          autoComplete="off"
                          placeholder="For the allergy card"
                          defaultValue={person.phone}
                          maxLength={40}
                          disabled={busy}
                          aria-label={`Phone number for ${person.name}`}
                          onBlur={(e) => void savePhone(person, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                          }}
                        />
                      </label>
                      <div className="person-access">
                        {person.access === 'member' || me?.id === person.id ? (
                          <span className="muted">Has an account</span>
                        ) : person.access === 'invited' ? (
                          <>
                            <span className="muted">Invited, not joined yet</span>
                            <button className="linklike" onClick={() => invite(person)} disabled={busy}>
                              Resend
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="muted">Emails only</span>
                            <button className="linklike" onClick={() => invite(person)} disabled={busy}>
                              Invite to plan together
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button className="btn sm" onClick={() => setAdding(true)}>
                + Add a parent or carer
              </button>

              {/*
                A dialog, not a form unfolding at the end of the page.

                Inline, the two fields and the Add button appeared below the
                fold of a long screen, and the keyboard then covered what
                little of them was showing -- so you filled in a name and had
                to scroll, blind, to find the rest. The dialog centres itself
                and already knows how tall the keyboard is.
              */}
              {adding && (
                <Modal
                  title="Add a parent or carer"
                  onClose={() => setAdding(false)}
                  footer={
                    <>
                      <button className="btn ghost" onClick={() => setAdding(false)} disabled={busy}>
                        Cancel
                      </button>
                      <div className="spacer" />
                      <button
                        className="btn primary"
                        onClick={addPerson}
                        disabled={busy || !newName.trim() || !newEmail.trim()}
                      >
                        {busy ? 'Adding…' : 'Add'}
                      </button>
                    </>
                  }
                >
                  <div className="field">
                    <label htmlFor="add-person-name">Name</label>
                    <input
                      id="add-person-name"
                      type="text"
                      autoComplete="off"
                      placeholder="Melanie"
                      value={newName}
                      maxLength={40}
                      onChange={(e) => setNewName(e.target.value)}
                      autoFocus
                    />
                    <span className="hint">As they sign in, if you invite them.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="add-person-email">Email</label>
                    <input
                      id="add-person-email"
                      type="email"
                      autoComplete="off"
                      placeholder="name@example.com"
                      value={newEmail}
                      maxLength={200}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void addPerson()
                      }}
                    />
                    <span className="hint">
                      Nothing is sent until you turn something on for them under Notifications.
                    </span>
                  </div>
                </Modal>
              )}
            </div>
          </section>
        )}

        {/*
          Both tabs render the same editor over the same draft, differing only
          in which groups they show, and both carry the same Save. An edit made
          on one is pending on the other, so hiding the Save on either would
          leave unsaved work behind a tab nobody was looking at.
        */}
        {(section === 'allergies' || section === 'restrictions') && (
          <section className="set-panel" aria-labelledby={`set-${section}`}>
            <h2 id={`set-${section}`} className="sr-only">
              {section === 'allergies' ? 'Allergies' : 'Restrictions'}
            </h2>
            <div className="field">
              <label>
                {section === 'allergies'
                  ? `${boot.profileName}’s allergies`
                  : `${boot.profileName}’s diet and school rules`}
              </label>
              {allergenSaved !== null && (
                <p className="muted" style={{ fontSize: 12.5, margin: '0 0 6px' }}>
                  {allergenSaved}
                </p>
              )}
              <AllergenPicker
                childName={boot.profileName}
                value={allergens}
                onChange={setAllergens}
                show={section === 'allergies' ? ['allergies'] : ['diets', 'school']}
              />
              {/*
                Pinned, and only once there is something to lose.
                
                The Save used to sit at the natural end of a screen you have to
                scroll -- past ten allergens, four diets and eleven school
                rules -- so it was easy to edit, look away, and leave with
                nothing stored and nothing said. Autosave would be the other
                answer and is wrong here: a ticked allergy with no severity yet
                is a state this app deliberately refuses to store, so saving on
                every keystroke would drop the row the parent just added.
              */}
              {allergensDirty && (
                <div className="set-savebar" role="status">
                  <span className="set-savebar-what">
                    {/*
                      The unanswered severity is always an allergy, so from the
                      restrictions tab the message has to say where to go and
                      not just that something is missing.
                    */}
                    {waiting.length > 0
                      ? section === 'allergies'
                        ? `Say how serious ${waiting.length === 1 ? 'it' : 'each one'} is first.`
                        : `Say how serious ${waiting.length === 1 ? 'an allergy' : 'each allergy'} is on the Allergies tab first.`
                      : 'Not saved yet.'}
                  </span>
                  <button className="btn sm" disabled={allergenBusy} onClick={() => setAllergens(asNew(boot.allergens))}>
                    Undo
                  </button>
                  <button
                    className="btn sm primary"
                    disabled={allergenBusy || waiting.length > 0}
                    onClick={saveAllergens}
                  >
                    {allergenBusy ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
              {/*
                The card prints both tabs, so it is offered from both.

                It used to be one grey line under the caveat, at the end of a
                screen nobody scrolls to the end of -- the most useful thing
                on the page, formatted like a footnote. It is now the thing it
                is: a card, showing what is actually on it, so the answer to
                "is this worth printing?" does not require printing it.
              */}
              <div className={`card-offer ${cardHasSevere ? 'severe' : ''}`}>
                <div className="card-offer-head">
                  <span className="card-offer-mark" aria-hidden="true">🖨</span>
                  <div>
                    <h3>A card for school</h3>
                    <p>
                      One page with everything on both tabs and who to call. For the classroom
                      wall, a babysitter, a grandparent, or to show at the door.
                    </p>
                  </div>
                </div>

                {/*
                  What is stored, not what is on screen -- the card prints the
                  former, and saying otherwise here is how you end up printing
                  a card that disagrees with the page that offered it.
                */}
                <dl className="card-offer-lines">
                  {cardLines.map(({ label, value, missing }) => (
                    <div key={label} className={missing ? 'missing' : ''}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="card-offer-foot">
                  <a className="btn primary" href="/allergy-card" target="_blank" rel="noopener">
                    Open the card
                  </a>
                  <span className="card-offer-note">
                    {allergensDirty
                      ? 'Save first — the card shows what is stored, not what is on screen.'
                      : 'Opens in a new tab, ready to print.'}
                  </span>
                </div>
              </div>
            </div>
          </section>
        )}

        {section === 'notifications' && (
          <section className="set-panel" aria-labelledby="set-notifications">
            <h2 id="set-notifications" className="sr-only">Notifications</h2>
            {/* Only here. It is a fact about email, and showing it above the
                theme buttons made it look like a warning about the page. */}
            {!boot.emailConfigured && (
              <div className="banner error">
                No Resend key is set on the Worker yet, so nothing will actually send. Add it with{' '}
                <code>wrangler secret put RESEND_API_KEY</code>, or in the Cloudflare dashboard
                under the Worker&rsquo;s Variables and Secrets.
              </div>
            )}

            <div className="field">
              <label>Weekly reminder</label>
              <p className="muted" style={{ fontSize: 12.5, margin: '0 0 4px' }}>
                A nudge when the coming week still has nothing in it. Skipped silently if it does.
              </p>
              <div className="row wrap">
                <button
                  className="btn sm"
                  aria-pressed={reminder.enabled}
                  onClick={() => patchReminder({ enabled: !reminder.enabled })}
                  disabled={busy}
                >
                  {reminder.enabled ? '✓ On' : 'Off'}
                </button>
                <select
                  aria-label="Reminder day"
                  value={reminder.weekday}
                  disabled={busy}
                  onChange={(e) => patchReminder({ weekday: Number(e.target.value) })}
                >
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Reminder time"
                  value={reminder.hour}
                  disabled={busy}
                  onChange={(e) => patchReminder({ hour: Number(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Timezone"
                  value={reminder.timezone}
                  disabled={busy}
                  onChange={(e) => patchReminder({ timezone: e.target.value })}
                >
                  {[...new Set([reminder.timezone, ...ZONES])].map((z) => (
                    <option key={z} value={z}>
                      {z.split('/').pop()!.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <button className="btn sm" onClick={checkNow} disabled={busy}>
                  Run the check now
                </button>
              </div>
            </div>

            {/*
              The mailing list, where the rest of the sending is.
              
              These toggles used to sit on the Family tab, under the people --
              so "why did nobody get the reminder?" was answered two tabs from
              the reminder itself. The people stay there, because who is in the
              family is not a fact about email.
            */}
            <div className="field" data-section="recipients">
              <label>Who gets emailed</label>
              {boot.people.length === 0 ? (
                <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
                  Nobody yet. Add someone under{' '}
                  <button className="linklike" onClick={() => go('family')}>
                    Family
                  </button>{' '}
                  and they can be emailed from here. Nothing is sent to anyone who is not listed.
                </p>
              ) : (
                <div className="mail-list">
                  {!reminder.enabled && (
                    <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
                      The weekly reminder is off above, so nobody is getting that one whatever
                      their switch says.
                    </p>
                  )}
                  {boot.people.map((person) => (
                    <div className="mail-row" key={person.id}>
                      <div className="mail-who">
                        <span className="title">
                          {person.name}
                          {me?.id === person.id && <span className="muted"> · you</span>}
                        </span>
                        <span className="mail-address">{person.email}</span>
                      </div>
                      <div className="mail-switches">
                        <button
                          className="btn sm"
                          aria-pressed={person.notifyReminder}
                          onClick={() => toggle(person, 'notifyReminder')}
                          disabled={busy}
                        >
                          {person.notifyReminder ? '✓ ' : ''}Weekly reminder
                        </button>
                        <button
                          className="btn sm"
                          aria-pressed={person.notifyReview}
                          onClick={() => toggle(person, 'notifyReview')}
                          disabled={busy}
                        >
                          {person.notifyReview ? '✓ ' : ''}Review requests
                        </button>
                        <button
                          className="btn sm ghost"
                          onClick={() => sendTest(person)}
                          disabled={busy || !boot.emailConfigured}
                          title="Send a test email to this address"
                        >
                          Test
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="field">
              <label>Recent email</label>
              {log === null ? (
                <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                  Loading…
                </p>
              ) : log.length === 0 ? (
                <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                  Nothing sent yet.
                </p>
              ) : (
                <div className="email-log">
                  {log.map((entry) => (
                    <div className={`email-log-row ${entry.status}`} key={entry.id}>
                      <span className="dot" aria-hidden="true">
                        {entry.status === 'sent' ? '●' : entry.status === 'failed' ? '▲' : '○'}
                      </span>
                      <span className="body">
                        <span className="title">
                          {KIND_LABEL[entry.kind] ?? entry.kind} → {entry.recipient}
                        </span>
                        <span className="sub">
                          {entry.createdAt}
                          {entry.status === 'failed' && entry.detail ? ` · ${entry.detail}` : ''}
                          {entry.status === 'sending' ? ' · never completed' : ''}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {section === 'app' && (
          <section className="set-panel" aria-labelledby="set-app">
            <h2 id="set-app" className="sr-only">App</h2>
            <div className="field">
              <label>Appearance</label>
              <div className="row wrap">
                {THEMES.map((option) => (
                  <button
                    key={option.value}
                    className="btn sm"
                    aria-pressed={theme.preference === option.value}
                    onClick={() => theme.setPreference(option.value)}
                    title={option.hint}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {theme.preference === 'auto' && (
                <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
                  Following your device, which is currently {theme.resolved}.
                </p>
              )}
            </div>

            <div className="field">
              <label>Feedback</label>
              <p className="muted" style={{ fontSize: 13.5, margin: '0 0 6px' }}>
                An idea, a bug, something confusing — it goes straight to the person who built this.
              </p>
              <button className="btn sm" onClick={onFeedback}>
                💬 Send feedback
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

/* ---- Children ------------------------------------------------------------ */

/**
 * Add a child, rename one, or take one out of the switcher.
 *
 * "Remove" archives. Everything a child ever ate stays, and so does every
 * allergy and school rule recorded for them, because taking someone out of a
 * dropdown should not be the same gesture as erasing their history -- and
 * there is no undo for the second. Restoring brings the lot back.
 */
/**
 * A name being changed, in place.
 *
 * Its own component so the field starts from the name it is editing and does
 * not need a second piece of state up in the page, where it would go stale
 * the moment the list reloaded underneath it.
 */
function RenamePerson({
  person,
  busy,
  onSave,
  onDone,
}: {
  person: Person
  busy: boolean
  onSave: (person: Person, name: string) => void
  onDone: () => void
}) {
  const [draft, setDraft] = useState(person.name)
  return (
    <div className="person-rename">
      <input
        type="text"
        autoComplete="off"
        value={draft}
        maxLength={40}
        disabled={busy}
        autoFocus
        aria-label={`Name for ${person.name}`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave(person, draft)
          if (e.key === 'Escape') onDone()
        }}
      />
      <button className="btn sm primary" disabled={busy || !draft.trim()} onClick={() => onSave(person, draft)}>
        Save
      </button>
      <button className="btn sm ghost" disabled={busy} onClick={onDone}>
        Cancel
      </button>
    </div>
  )
}

function ChildList({
  onError,
  onChanged,
}: {
  onError: (err: unknown, fallback: string) => void
  onChanged: (children: Child[]) => void
}) {
  const [children, setChildren] = useState<Child[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  /*
   * Removing used to happen on the tap, with nothing said. It is reversible
   * -- the row is archived and Bring back returns the lot -- but nothing on
   * screen said so, so it read like deleting a child and their history, which
   * is a thing worth being sure about before you do it.
   */
  const [removing, setRemoving] = useState<Child | null>(null)

  const load = useCallback(
    async (all: boolean) => {
      try {
        setChildren(await api.children(all))
      } catch (err) {
        onError(err, 'Could not load the children.')
      }
    },
    [onError],
  )
  useEffect(() => {
    void load(showArchived)
  }, [load, showArchived])

  /** Every change hands the live list back up, so the switcher keeps pace. */
  const settle = (next: Child[]) => {
    setChildren(showArchived ? null : next)
    if (showArchived) void load(true)
    onChanged(next.filter((k) => !k.archived))
  }

  const run = async (what: () => Promise<Child[]>, fallback: string) => {
    if (busy) return
    setBusy(true)
    try {
      settle(await what())
    } catch (err) {
      onError(err, fallback)
    } finally {
      setBusy(false)
    }
  }

  if (!children) return <p className="muted small">Loading…</p>

  const live = children.filter((k) => !k.archived)

  return (
    <>
      <ul className="child-list">
        {children.map((k) => (
          <li key={k.id} className={`child-row ${k.archived ? 'archived' : ''}`}>
            {editing === k.id ? (
              <>
                <input
                  value={draft}
                  autoFocus
                  aria-label={`Rename ${k.name}`}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditing(null)
                      void run(() => api.renameChild(k.id, draft), 'Could not rename them.')
                    }
                    if (e.key === 'Escape') setEditing(null)
                  }}
                />
                <button
                  className="btn sm primary"
                  disabled={busy || !draft.trim()}
                  onClick={() => {
                    setEditing(null)
                    void run(() => api.renameChild(k.id, draft), 'Could not rename them.')
                  }}
                >
                  Save
                </button>
                <button className="btn sm ghost" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="child-name">
                  {k.name}
                  {k.archived && <span className="muted small"> · removed</span>}
                </span>
                <div className="spacer" />
                {k.archived ? (
                  <button className="btn sm" disabled={busy} onClick={() => void run(() => api.restoreChild(k.id), 'Could not bring them back.')}>
                    Bring back
                  </button>
                ) : (
                  <>
                    <button
                      className="btn sm ghost"
                      disabled={busy}
                      onClick={() => {
                        setEditing(k.id)
                        setDraft(k.name)
                      }}
                    >
                      Rename
                    </button>
                    {/* The last one cannot go: there would be no board left. */}
                    {live.length > 1 && (
                      <button className="btn sm ghost" disabled={busy} onClick={() => setRemoving(k)}>
                        Remove
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="row">
          <input
            value={name}
            autoFocus
            aria-label="New child’s first name"
            placeholder="First name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setAdding(false)
              if (e.key === 'Enter' && name.trim()) {
                setAdding(false)
                const n = name
                setName('')
                void run(async () => (await api.addChild(n)).profiles, 'Could not add them.')
              }
            }}
          />
          <button
            className="btn sm primary"
            disabled={busy || !name.trim()}
            onClick={() => {
              setAdding(false)
              const n = name
              setName('')
              void run(async () => (await api.addChild(n)).profiles, 'Could not add them.')
            }}
          >
            Add
          </button>
          <button className="btn sm ghost" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="row">
          <button className="btn sm" onClick={() => setAdding(true)}>
            Add a child
          </button>
          <div className="spacer" />
          <button className="btn sm ghost" aria-pressed={showArchived} onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide removed' : 'Show removed'}
          </button>
        </div>
      )}
      <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
        Each child has their own board, allergies and school rules. The pantry is
        the family&rsquo;s, shared between them.
      </p>

      {/*
        Asked, and answered honestly.

        Removing is an archive: the boards, the weeks, the allergies and the
        school rules all stay, and Bring back returns them. Saying so is the
        point of the dialog -- a parent hesitating over this button is afraid
        of erasing a child's history, and the truthful answer is that they
        cannot, not from here. Typing the name to confirm would say the
        opposite about a thing that undoes in one tap.
      */}
      {removing && (
        <Modal
          title={`Remove ${removing.name}?`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn ghost" onClick={() => setRemoving(null)} disabled={busy}>
                Cancel
              </button>
              <div className="spacer" />
              <button
                className="btn danger"
                disabled={busy}
                onClick={() => {
                  const k = removing
                  setRemoving(null)
                  void run(() => api.archiveChild(k.id), 'Could not remove them.')
                }}
              >
                {busy ? 'Removing…' : `Remove ${removing.name}`}
              </button>
            </>
          }
        >
          <p>
            {removing.name} comes out of the switcher, and you stop planning lunches for them.
          </p>
          <p className="muted" style={{ fontSize: 13.5 }}>
            Nothing is deleted. Every week you planned for {removing.name}, and their allergies,
            restrictions and school rules, stay exactly as they are — <b>Show removed</b> brings
            them back whenever you want.
          </p>
        </Modal>
      )}
    </>
  )
}
